import { ArrowDropUp, Clear, Remove } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import { Brick } from "components/Brick";

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
  maximumSignificantDigits: 4,
  useGrouping: false,
});

const MIN_PERCENTAGE_TO_SHOW = 0.001;

const PercentageBrick = ({ percentage }: { percentage: number }) => {
  const theme = useTheme();

  const averageToFormat =
    percentage >= MIN_PERCENTAGE_TO_SHOW || percentage <= -MIN_PERCENTAGE_TO_SHOW ? percentage : 0;

  return (
    <Brick variant={averageToFormat === 0 ? "dark" : averageToFormat > 0 ? "green" : "red"}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          position: "relative",
          minWidth: "52px",
        }}>
        {averageToFormat !== 0 ? (
          <ArrowDropUp
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              transform: percentage < 0 ? "rotate(180deg)" : "none",
            }}
          />
        ) : (
          <Remove
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              width: "12px",
              marginLeft: "4px",
            }}
          />
        )}
        <Typography variant="body-sm" marginLeft={averageToFormat !== 0 ? theme.spacing(2) : 0}>
          {numberFormatter.format(+Math.abs(averageToFormat * 100).toFixed(1)) == "0"
            ? "0.0"
            : (+numberFormatter.format(+Math.abs(averageToFormat * 100).toFixed(1))).toFixed(1)}
          %
        </Typography>
      </Box>
    </Brick>
  );
};

const PricePerformanceBrick = ({ performance }: { performance: number }) => {
  const theme = useTheme();

  const averageToFormat =
    performance >= MIN_PERCENTAGE_TO_SHOW || performance <= -MIN_PERCENTAGE_TO_SHOW
      ? performance
      : 0;

  return (
    <Brick variant={averageToFormat === 0 ? "dark" : averageToFormat > 0 ? "green" : "red"}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          position: "relative",
          minWidth: "52px",
        }}>
        {averageToFormat !== 0 ? (
          <ArrowDropUp
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              transform: performance < 0 ? "rotate(180deg)" : "none",
            }}
          />
        ) : (
          <Remove
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              width: "12px",
              marginLeft: "4px",
            }}
          />
        )}
        <Typography variant="body-sm" marginLeft={averageToFormat !== 0 ? theme.spacing(2) : 0}>
          {numberFormatter.format(Number(performance.toFixed(2))) == "0"
            ? "$0.00"
            : `$${(+numberFormatter.format(Number(performance.toFixed(2)))).toFixed(2)}`.replace(
                "$-",
                "$",
              )}
        </Typography>
      </Box>
    </Brick>
  );
};

const ValuePerformanceBrick = ({ value }: { value: number }) => {
  const theme = useTheme();

  const averageToFormat = value >= 1 || value <= -1 ? value : 0;

  return (
    <Brick variant={averageToFormat === 0 ? "dark" : averageToFormat > 0 ? "green" : "red"}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          position: "relative",
          minWidth: "52px",
        }}>
        {averageToFormat !== 0 ? (
          <ArrowDropUp
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              transform: value < 0 ? "rotate(180deg)" : "none",
            }}
          />
        ) : (
          <Remove
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              width: "12px",
              marginLeft: "4px",
            }}
          />
        )}
        <Typography variant="body-sm" marginLeft={averageToFormat !== 0 ? theme.spacing(2) : 0}>
          {numberFormatter.format(+Math.abs(value)) == "0"
            ? "0"
            : +numberFormatter.format(+Math.abs(value))}
        </Typography>
      </Box>
    </Brick>
  );
};

const PercentageBlock = ({ percentage }: { percentage: number }) => {
  const theme = useTheme();

  const averageToFormat =
    percentage >= MIN_PERCENTAGE_TO_SHOW || percentage <= -MIN_PERCENTAGE_TO_SHOW ? percentage : 0;

  return (
    <Box
      padding="2px 4px"
      borderRadius="8px"
      border={`1px solid ${theme.palette.border.dark}`}
      bgcolor={
        averageToFormat === 0
          ? theme.palette.surface["silver"]
          : averageToFormat > 0
          ? theme.palette.system["green"]
          : theme.palette.system["red"]
      }>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          position: "relative",
          minWidth: "52px",
        }}>
        {averageToFormat !== 0 ? (
          <ArrowDropUp
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              transform: percentage < 0 ? "rotate(180deg)" : "none",
            }}
          />
        ) : (
          <Remove
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              width: "12px",
              marginLeft: "4px",
            }}
          />
        )}
        <Typography variant="body-sm" marginLeft={averageToFormat !== 0 ? theme.spacing(2) : 0}>
          {numberFormatter.format(+Math.abs(averageToFormat * 100).toFixed(1)) == "0"
            ? "0.0"
            : (+numberFormatter.format(+Math.abs(averageToFormat * 100).toFixed(1))).toFixed(1)}
          %
        </Typography>
      </Box>
    </Box>
  );
};

const PricePerformanceBlock = ({ performance }: { performance: number }) => {
  const theme = useTheme();

  const averageToFormat =
    performance >= MIN_PERCENTAGE_TO_SHOW || performance <= -MIN_PERCENTAGE_TO_SHOW
      ? performance
      : 0;

  return (
    <Box
      padding="4px"
      borderRadius="8px"
      border={`1px solid ${theme.palette.border.dark}`}
      bgcolor={
        averageToFormat === 0
          ? theme.palette.surface["silver"]
          : averageToFormat > 0
          ? theme.palette.system["green"]
          : theme.palette.system["red"]
      }>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          position: "relative",
          minWidth: "52px",
        }}>
        {averageToFormat !== 0 ? (
          <ArrowDropUp
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              transform: performance < 0 ? "rotate(180deg)" : "none",
            }}
          />
        ) : (
          <Remove
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              width: "12px",
              marginLeft: "4px",
            }}
          />
        )}
        <Typography variant="body-sm" marginLeft={averageToFormat !== 0 ? theme.spacing(2) : 0}>
          {numberFormatter.format(Number(performance.toFixed(2))) == "0"
            ? "$0.00"
            : `$${(+numberFormatter.format(Number(performance.toFixed(2)))).toFixed(2)}`.replace(
                "$-",
                "$",
              )}
        </Typography>
      </Box>
    </Box>
  );
};

const PricePerformanceWithPercentagBlock = ({
  performance,
  percentage,
}: {
  performance: number;
  percentage: number;
}) => {
  const theme = useTheme();

  const averageToFormat =
    performance >= MIN_PERCENTAGE_TO_SHOW || performance <= -MIN_PERCENTAGE_TO_SHOW
      ? performance
      : 0;

  const averageToFormatInPercentage =
    percentage >= MIN_PERCENTAGE_TO_SHOW || percentage <= -MIN_PERCENTAGE_TO_SHOW ? percentage : 0;

  return (
    <Box
      padding="4px"
      borderRadius="8px"
      border={`1px solid ${theme.palette.border.dark}`}
      bgcolor={
        averageToFormat === 0
          ? theme.palette.surface["silver"]
          : averageToFormat > 0
          ? theme.palette.system["green"]
          : theme.palette.system["red"]
      }>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          position: "relative",
          minWidth: "52px",
        }}>
        {averageToFormat !== 0 ? (
          <ArrowDropUp
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              transform: performance < 0 ? "rotate(180deg)" : "none",
            }}
          />
        ) : (
          <Remove
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              width: "12px",
              marginLeft: "4px",
            }}
          />
        )}
        <Typography variant="body-sm" marginLeft={averageToFormat !== 0 ? theme.spacing(2) : 0}>
          {numberFormatter.format(Number(performance.toFixed(2))) == "0"
            ? "$0.00"
            : `$${(+numberFormatter.format(Number(performance.toFixed(2)))).toFixed(2)}`.replace(
                "$-",
                "$",
              )}{" "}
          (
          {numberFormatter.format(+Math.abs(averageToFormatInPercentage * 100).toFixed(1)) == "0"
            ? "0.0"
            : (+numberFormatter.format(
                +Math.abs(averageToFormatInPercentage * 100).toFixed(1),
              )).toFixed(1)}
          %)
        </Typography>
      </Box>
    </Box>
  );
};

const ValuePerformanceBlock = ({ value }: { value: number }) => {
  const theme = useTheme();

  const averageToFormat = value >= 1 || value <= -1 ? value : 0;

  return (
    <Box
      marginLeft={theme.spacing(1)}
      padding="2px 4px"
      borderRadius="8px"
      border={`1px solid ${theme.palette.border.dark}`}
      bgcolor={
        averageToFormat === 0
          ? theme.palette.surface["silver"]
          : averageToFormat > 0
          ? theme.palette.system["green"]
          : theme.palette.system["red"]
      }>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          position: "relative",
          minWidth: "52px",
        }}>
        {averageToFormat !== 0 ? (
          <ArrowDropUp
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              transform: value < 0 ? "rotate(180deg)" : "none",
            }}
          />
        ) : (
          <Remove
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              width: "12px",
              marginLeft: "4px",
            }}
          />
        )}
        <Typography variant="body-sm" marginLeft={averageToFormat !== 0 ? theme.spacing(2) : 0}>
          {numberFormatter.format(+Math.abs(value)) == "0"
            ? "0"
            : +numberFormatter.format(+Math.abs(value))}
        </Typography>
      </Box>
    </Box>
  );
};

const XPerformanceBlock = ({ xPerformance }: { xPerformance: number }) => {
  const theme = useTheme();
  const THRESHOLD = 0.01;

  const averageToFormat =
    xPerformance >= 1 + THRESHOLD || xPerformance <= 1 - THRESHOLD ? xPerformance : 1;

  return (
    <Box
      padding="2px 4px"
      borderRadius="8px"
      border={`1px solid ${theme.palette.border.dark}`}
      bgcolor={
        averageToFormat === 0
          ? theme.palette.surface["silver"]
          : averageToFormat > 0
          ? theme.palette.system["green"]
          : theme.palette.system["red"]
      }>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          paddingX: "2px",
          paddingY: "1px",
          alignItems: "center",
          position: "relative",
          width: "60px",
        }}>
        {averageToFormat !== 1 ? (
          <ArrowDropUp
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              transform: xPerformance < 1 ? "rotate(180deg)" : "none",
            }}
          />
        ) : (
          <Remove
            sx={{
              position: "absolute",
              left: theme.spacing(-0.75),
              width: "12px",
              marginLeft: "4px",
            }}
          />
        )}

        {/* <Typography variant="body-sm">
          <b>X</b>
        </Typography> */}
        <Typography variant="body-sm">
          {numberFormatter.format(+Math.abs(averageToFormat).toFixed(2)) == "0"
            ? "0.00"
            : (+numberFormatter.format(+Math.abs(averageToFormat).toFixed(2))).toFixed(2)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", maxHeight: "12px" }}>
          <Clear sx={{ width: "12px", height: "12px" }} />
        </Box>
      </Box>
    </Box>
  );
};

export {
  PercentageBrick,
  PricePerformanceBrick,
  PercentageBlock,
  ValuePerformanceBrick,
  PricePerformanceBlock,
  PricePerformanceWithPercentagBlock,
  ValuePerformanceBlock,
  XPerformanceBlock,
};
